import { useState, useEffect } from "react";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Plus, Pencil, UserX, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const load = () => API.get("/users").then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (currentUser?.role !== "admin") {
    return (
      <>
        <TopBar title="Users" />
        <div className="flex-1 flex items-center justify-center text-[#686B74]">Admin access required</div>
      </>
    );
  }

  const openCreate = () => { setEditUser(null); setForm({ name: "", email: "", password: "", role: "user" }); setShowDialog(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      if (editUser) {
        const updates = { name: form.name, email: form.email, role: form.role };
        await API.put(`/users/${editUser.id}`, updates);
        toast.success("User updated");
      } else {
        if (!form.password || form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        await API.post("/users", form);
        toast.success("User created");
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save user");
    }
  };

  const handleDeactivate = async (id) => {
    await API.delete(`/users/${id}`);
    toast.success("User deactivated");
    load();
  };

  const handlePermanentDelete = async (id) => {
    try {
      const { data } = await API.post(`/users/${id}/delete-permanent`);
      toast.success(data.message);
      setShowDeleteConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <>
      <TopBar title="Users" />
      <div className="flex-1 overflow-y-auto p-6" data-testid="users-page">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#686B74]">{users.length} user{users.length !== 1 ? "s" : ""}</p>
          <Button data-testid="create-user-btn" onClick={openCreate} size="sm" className="bg-[#111216] hover:bg-[#2B2D31] text-white">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New User
          </Button>
        </div>

        <div className="bg-white border border-[#EBEBEB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#EBEBEB] bg-[#F9F9FB]">
                <th className="text-left px-4 py-2 text-xs font-medium text-[#686B74]">User</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-[#686B74]">Email</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-[#686B74]">Role</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-[#686B74]">Status</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-[#686B74]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#EBEBEB] last:border-0 hover:bg-[#F9F9FB] transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={u.avatar_url} />
                        <AvatarFallback className="text-[10px] bg-[#111216] text-white">{u.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[#686B74]">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={`text-[11px] ${u.role === "admin" ? "bg-[#EDF0FF] text-[#4353FF] border-[#4353FF]/20" : ""}`}>
                      {u.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={`text-[11px] ${u.status === "active" ? "bg-green-50 text-green-600 border-green-200" : "bg-red-50 text-red-500 border-red-200"}`}>
                      {u.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(u)} className="p-1.5 text-[#686B74] hover:text-[#111216] transition-colors" data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {u.id !== currentUser.id && u.status === "active" && (
                        <button onClick={() => handleDeactivate(u.id)} className="p-1.5 text-[#686B74] hover:text-[#E24A4A] transition-colors" data-testid={`deactivate-user-${u.id}`}>
                          <UserX className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {u.id !== currentUser.id && (
                        <button onClick={() => setShowDeleteConfirm(u)} className="p-1.5 text-[#686B74] hover:text-[#E24A4A] transition-colors" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editUser ? "Edit User" : "Create User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1 block">Name</label>
              <Input data-testid="user-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1 block">Email</label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@company.com" />
            </div>
            {!editUser && (
              <div>
                <label className="text-xs font-medium text-[#686B74] mb-1 block">Password</label>
                <Input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1 block">Role</label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="user-save-btn" onClick={handleSave} className="bg-[#111216] hover:bg-[#2B2D31] text-white">
              {editUser ? "Save Changes" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#E24A4A]">Permanently Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#111216]">
              Are you sure you want to permanently delete <span className="font-semibold">{showDeleteConfirm?.name}</span>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              This action will:
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Permanently remove the user account</li>
                <li>Delete all open/allocated tasks owned by this user</li>
                <li>Remove them from all groups</li>
                <li>Delete their notifications</li>
              </ul>
              <p className="mt-2 font-medium">This cannot be undone.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button
              data-testid="confirm-delete-user-btn"
              onClick={() => handlePermanentDelete(showDeleteConfirm.id)}
              className="bg-[#E24A4A] hover:bg-red-600 text-white"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
